import { useTranslation } from '@repo/i18n'
import { X } from 'lucide-react'
import type { AsyncPickerSource } from '#commons/async-pickers'
import { AsyncPickerList, AsyncPickerRoot } from '#commons/async-pickers'
import { Button } from '#components/button'
import { ButtonGroup } from '#components/button-group'
import {
	ComboboxContent,
	ComboboxInput,
	ComboboxTrigger,
	useComboboxAnchor
} from '#components/combobox'
import { cn } from '#lib/utils'

function InlineAsyncFilterTrigger(props: {
	applied: boolean
	label: string
	value?: React.ReactNode
	onClear: () => void
}) {
	const translation = useTranslation()

	const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		event.stopPropagation()
		props.onClear()
	}

	return (
		<ButtonGroup className='min-w-0 max-w-full'>
			<ComboboxTrigger
				aria-label={props.label}
				className='min-w-0'
				render={
					<Button
						className='shrink'
						type='button'
						variant='outline'
					/>
				}
				title={
					typeof props.value === 'string' ? props.value : undefined
				}
			>
				<span className='min-w-0 truncate'>
					{props.applied ? (
						<>
							<span>{props.label}</span>
							<span aria-hidden='true'>: </span>
							<span>{props.value}</span>
						</>
					) : (
						props.label
					)}
				</span>
			</ComboboxTrigger>
			{props.applied && (
				<Button
					aria-label={translation.t(
						'shared:inline_filters.clear_label',
						{ label: props.label }
					)}
					onClick={handleClear}
					size='icon'
					type='button'
					variant='outline'
				>
					<X />
				</Button>
			)}
		</ButtonGroup>
	)
}

/**
 * Async single-value filter powered by the shared async picker primitives.
 * The combobox owns its popup so focus, highlighting, and positioning stay
 * aligned with the form pickers.
 */
export function InlineFilterAsyncSelect<T>(props: {
	label: string
	contentClassName?: string
	source: AsyncPickerSource<T>
	value: T | null
	onValueChange: (value: T | null) => void
}) {
	const anchor = useComboboxAnchor()

	return (
		<AsyncPickerRoot
			onValueChange={props.onValueChange}
			searchInputValue
			source={props.source}
			value={props.value}
		>
			<div className='min-w-0 max-w-full sm:max-w-80' ref={anchor}>
				<InlineAsyncFilterTrigger
					applied={props.value !== null}
					label={props.label}
					onClear={() => {
						props.onValueChange(null)
					}}
					value={
						props.value !== null
							? props.source.getLabel(props.value)
							: undefined
					}
				/>
			</div>
			<ComboboxContent
				anchor={anchor}
				className={cn(
					'flex w-72 flex-col [&>[data-slot=input-group]]:shrink-0',
					props.contentClassName
				)}
			>
				<ComboboxInput
					aria-label={props.label}
					placeholder={props.source.placeholder}
					showTrigger={false}
				/>
				<AsyncPickerList />
			</ComboboxContent>
		</AsyncPickerRoot>
	)
}

/**
 * Async multi-value filter powered by the shared async picker primitives.
 */
export function InlineFilterAsyncMultiSelect<T>(props: {
	label: string
	source: AsyncPickerSource<T>
	value: T[]
	onValueChange: (value: T[]) => void
}) {
	const translation = useTranslation()
	const anchor = useComboboxAnchor()

	return (
		<AsyncPickerRoot<T>
			multiple
			onValueChange={props.onValueChange}
			searchInputValue
			source={props.source}
			value={props.value}
		>
			<div className='min-w-0 max-w-full sm:max-w-80' ref={anchor}>
				<InlineAsyncFilterTrigger
					applied={props.value.length > 0}
					label={props.label}
					onClear={() => {
						props.onValueChange([])
					}}
					value={
						props.value.length
							? translation.t(
									'shared:async_pickers.selected_count',
									{
										count: props.value.length
									}
								)
							: undefined
					}
				/>
			</div>
			<ComboboxContent
				anchor={anchor}
				className='flex w-80 flex-col [&>[data-slot=input-group]]:shrink-0'
			>
				<ComboboxInput
					aria-label={props.label}
					placeholder={props.source.placeholder}
					showTrigger={false}
				/>
				<AsyncPickerList />
			</ComboboxContent>
		</AsyncPickerRoot>
	)
}
